import { useCallback, useEffect, useState } from 'react';

import {
  SatelliteConversationReadRepliedStatusTypeV2,
  useLatestConversationsForInbox,
} from '@zg-rentals/satellite-components/hooks/useLatestConversationsForInbox';

import type { ListingAlias } from '@zg-rentals/satellite-components/hooks/useLatestConversationsForInbox';

const TWO_WEEKS_AGO = 14;
const MAX_LEADS = 100;

type GhostedLead = {
  name: string;
  conversationId: string;
  isIncluded: boolean;
};

export const useGhostedLeads = (listingAlias: string, isMultifamily?: boolean) => {
  const [excludedConversationIds, setExcludedConversationIds] = useState<Set<string>>(new Set());

  const { data, isLoading: isFetchingGhostedLeads } = useLatestConversationsForInbox(
    {
      clientTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      daysBack: TWO_WEEKS_AGO,
      listingAlias: listingAlias as ListingAlias,
      readRepliedStatus: SatelliteConversationReadRepliedStatusTypeV2.NOT_REPLIED_TO,
      limit: MAX_LEADS,
    },
    { enabled: !isMultifamily && !!listingAlias },
  );

  const ghostedLeads: Array<GhostedLead> = (data?.conversations ?? []).map((conversation) => ({
    name: conversation.renterName ?? conversation.activeParticipants[0]?.name ?? '',
    conversationId: conversation.conversationId,
    isIncluded: !excludedConversationIds.has(conversation.conversationId),
  }));

  const toggleIncludeLead = useCallback(
    (i: number) => {
      const conversationId = data?.conversations?.[i]?.conversationId;
      if (!conversationId) return;
      setExcludedConversationIds((prev) => {
        const next = new Set(prev);
        if (next.has(conversationId)) {
          next.delete(conversationId);
        } else {
          next.add(conversationId);
        }
        return next;
      });
    },
    [data?.conversations],
  );

  return { ghostedLeads, toggleIncludeLead, isFetchingGhostedLeads };
};

export const useNotAvailableMessage = (street?: string, name?: string) => {
  const [notAvailableMessage, setNotAvailableMessage] = useState('');

  useEffect(() => {
    setNotAvailableMessage(`Hello,

The ${street} property you inquired about is no longer available. Thank you for your interest.

Best regards,

${name}`);
  }, [street, name]);

  return { notAvailableMessage, setNotAvailableMessage };
};
